/**
 * DecisionFlow — Reset Password
 * ui/src/screens/ResetPassword.jsx
 * data-fit LLC · S664 · July 31, 2026
 *
 * Public route: /reset-password?token=<raw_token>
 * No auth required. Consumes password_reset or platform_reset token.
 * Calls POST /api/v1/auth/reset-password.
 * On success → navigate to /login with success message in location.state.
 *
 * spec_auth_identity_s661.md §3.3 — reset password (token consumption)
 */

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "/api/v1";

// Password complexity: 10+ chars, uppercase, lowercase, digit
// Mirrors backend credential_service.py _check_password_complexity()
function checkComplexity(pw) {
  if (pw.length < 10)          return "Password must be at least 10 characters.";
  if (!/[A-Z]/.test(pw))      return "Password must contain at least one uppercase letter.";
  if (!/[a-z]/.test(pw))      return "Password must contain at least one lowercase letter.";
  if (!/[0-9]/.test(pw))      return "Password must contain at least one number.";
  return null;
}

function errorFromCode(code, message) {
  switch (code) {
    case "TOKEN_INVALID":
      return "This reset link is invalid or has already been used. Request a new one.";
    case "PASSWORD_COMPLEXITY_VIOLATION":
      return "Password does not meet complexity requirements.";
    case "PASSWORD_REUSE_DENIED":
      return "New password cannot be the same as your current password.";
    default:
      return message || "Something went wrong. Please try again.";
  }
}

export default function ResetPassword() {
  const [password,        setPassword]        = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting,      setSubmitting]      = useState(false);
  const [error,           setError]           = useState(null);
  const [showPassword,    setShowPassword]    = useState(false);

  const navigate  = useNavigate();
  const location  = useLocation();

  // Token delivered via email link: /reset-password?token=<raw>
  const token = new URLSearchParams(location.search).get("token") ?? "";

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password !== passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }

    const complexityError = checkComplexity(password);
    if (complexityError) {
      setError(complexityError);
      return;
    }

    if (!token) {
      setError("Reset token is missing. Use the link from your email.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          token,
          password,
          password_confirm: passwordConfirm,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const code = data?.error?.code ?? "";
        const msg  = data?.error?.message ?? "";
        setError(errorFromCode(code, msg));
        return;
      }

      // Success — navigate to login with a success banner
      navigate("/login", {
        replace: true,
        state: { successMessage: "Password updated. Please sign in with your new password." },
      });
    } catch (_) {
      setError("Unable to connect. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // No token in URL — show a clear message immediately
  const missingToken = !token;

  // ── Scoped CSS ──────────────────────────────────────────────────────────────
  const css = `
    .dfr-root {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: rgb(var(--color-bg));
      font-family: "IBM Plex Sans", "Segoe UI", Arial, sans-serif;
      padding: 24px;
    }

    .dfr-card {
      width: 100%;
      max-width: 420px;
      background: rgb(var(--color-surface));
      border: 1px solid rgb(var(--color-border));
      border-radius: 14px;
      padding: 40px 40px 36px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.06);
    }

    .dfr-identity { margin-bottom: 28px; }
    .dfr-identity-brand {
      font-size: var(--font-sz-xs);
      font-weight: 500;
      color: rgb(var(--color-text-muted));
      letter-spacing: 0.04em;
      margin-bottom: 3px;
    }
    .dfr-identity-name {
      font-size: var(--font-sz-xl);
      font-weight: 700;
      color: rgb(var(--color-primary));
      letter-spacing: -0.01em;
      line-height: 1.1;
    }

    .dfr-header { margin-bottom: 24px; }
    .dfr-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .dfr-subtitle {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      line-height: 1.5;
    }

    .dfr-error-banner {
      background: rgba(var(--color-danger-rgb, 220,38,38), 0.08);
      border: 1px solid rgba(var(--color-danger-rgb, 220,38,38), 0.2);
      border-radius: 8px;
      padding: 10px 14px;
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-danger));
      margin-bottom: 20px;
      line-height: 1.5;
    }

    .dfr-field { margin-bottom: 18px; }
    .dfr-label {
      display: block;
      font-size: var(--font-sz-sm);
      font-weight: 500;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .dfr-input-wrap {
      position: relative;
      display: flex;
      align-items: center;
    }
    .dfr-input {
      width: 100%;
      padding: 10px 40px 10px 13px;
      border: 1px solid rgb(var(--color-border));
      border-radius: 7px;
      background: rgb(var(--color-surface));
      color: rgb(var(--color-text));
      font-size: var(--font-sz-sm);
      font-family: inherit;
      transition: border-color 150ms, box-shadow 150ms;
      outline: none;
      box-sizing: border-box;
    }
    .dfr-input::placeholder {
      color: rgb(var(--color-placeholder));
      font-style: italic;
    }
    .dfr-input:focus {
      border-color: rgb(var(--color-focus-ring));
      box-shadow: 0 0 0 3px rgba(var(--color-focus-ring), 0.15);
    }
    .dfr-input:disabled { opacity: 0.5; cursor: not-allowed; }

    .dfr-reveal {
      position: absolute;
      right: 10px;
      background: none;
      border: none;
      cursor: pointer;
      color: rgb(var(--color-text-muted));
      font-size: var(--font-sz-xs);
      padding: 4px;
      font-family: inherit;
      line-height: 1;
    }
    .dfr-reveal:hover { color: rgb(var(--color-text)); }

    .dfr-hint {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      margin-top: 5px;
      line-height: 1.4;
    }

    .dfr-submit {
      width: 100%;
      padding: 12px;
      background: rgb(var(--color-primary));
      color: #ffffff;
      border: none;
      border-radius: 7px;
      font-size: var(--font-sz-base);
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms;
      letter-spacing: 0.01em;
      margin-bottom: 16px;
    }
    .dfr-submit:hover:not(:disabled) { background: rgb(var(--color-primary-hover)); }
    .dfr-submit:disabled { opacity: 0.5; cursor: not-allowed; }

    .dfr-back {
      display: block;
      text-align: center;
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      cursor: pointer;
      background: none;
      border: none;
      font-family: inherit;
      padding: 0;
      width: 100%;
    }
    .dfr-back:hover { color: rgb(var(--color-text)); text-decoration: underline; }

    /* Invalid link state */
    .dfr-invalid-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(var(--color-danger-rgb, 220,38,38), 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      font-size: 22px;
    }
    .dfr-invalid-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      text-align: center;
      margin-bottom: 10px;
    }
    .dfr-invalid-body {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      text-align: center;
      line-height: 1.6;
      margin-bottom: 28px;
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="dfr-root">
        <div className="dfr-card">

          {/* Product identity */}
          <div className="dfr-identity">
            <div className="dfr-identity-brand">data-fit LLC</div>
            <div className="dfr-identity-name">DecisionFlow</div>
          </div>

          {missingToken ? (
            /* ── Invalid / missing token state ── */
            <>
              <div className="dfr-invalid-icon" aria-hidden="true">⚠</div>
              <div className="dfr-invalid-title">Invalid reset link</div>
              <div className="dfr-invalid-body">
                This link is missing a reset token. Use the link from your
                password reset email, or request a new one.
              </div>
              <button
                className="dfr-submit"
                onClick={() => navigate("/forgot-password")}
              >
                Request a new link
              </button>
              <button
                className="dfr-back"
                onClick={() => navigate("/login")}
                type="button"
              >
                ← Back to sign in
              </button>
            </>
          ) : (
            /* ── Reset form ── */
            <>
              <div className="dfr-header">
                <div className="dfr-title">Set a new password</div>
                <div className="dfr-subtitle">
                  Choose a strong password. The link expires after one use.
                </div>
              </div>

              {error && (
                <div className="dfr-error-banner" role="alert">{error}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="dfr-field">
                  <label className="dfr-label" htmlFor="dfr-password">
                    New password
                  </label>
                  <div className="dfr-input-wrap">
                    <input
                      id="dfr-password"
                      className="dfr-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      autoFocus
                      required
                      disabled={submitting}
                      placeholder="••••••••••"
                    />
                    <button
                      type="button"
                      className="dfr-reveal"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  <div className="dfr-hint">
                    10+ characters · uppercase · lowercase · number
                  </div>
                </div>

                <div className="dfr-field">
                  <label className="dfr-label" htmlFor="dfr-confirm">
                    Confirm new password
                  </label>
                  <div className="dfr-input-wrap">
                    <input
                      id="dfr-confirm"
                      className="dfr-input"
                      type={showPassword ? "text" : "password"}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      autoComplete="new-password"
                      required
                      disabled={submitting}
                      placeholder="••••••••••"
                    />
                  </div>
                </div>

                <button
                  className="dfr-submit"
                  type="submit"
                  disabled={submitting || !password || !passwordConfirm}
                >
                  {submitting ? "Updating password…" : "Set new password"}
                </button>
              </form>

              <button
                className="dfr-back"
                onClick={() => navigate("/login")}
                type="button"
              >
                ← Back to sign in
              </button>
            </>
          )}

        </div>
      </div>
    </>
  );
}
